/**
 * The team's documents a bug, a task or an SDD points at — and, for those who may, pointing it at
 * another. The same links the documentation page shows from the document's side.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Link2, X } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { useDebounced } from '../lib/useDebounced'
import type { LinkedDoc } from '../lib/types'

export function LinkedDocs({ taskId, specificationId, canEdit }: { taskId?: string; specificationId?: string; canEdit: boolean }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [picking, setPicking] = useState(false)
  const [search, setSearch] = useState('')
  const term = useDebounced(search.trim(), 250)

  const { data: linked } = useQuery({
    queryKey: ['docs', 'linked', taskId ?? null, specificationId ?? null],
    queryFn: () => api.docs.linked({ taskId, specificationId }),
  })
  const { data: hits } = useQuery({
    queryKey: ['docs', 'search', term],
    queryFn: () => api.docs.search(term),
    enabled: picking && term.length >= 2,
  })
  const { data: tree } = useQuery({
    queryKey: ['docs', 'tree'],
    queryFn: api.docs.tree,
    enabled: picking,
  })

  const already = new Set((linked ?? []).map((doc) => doc.id))
  const options = (
    term.length >= 2
      ? (hits ?? []).map((hit) => ({ id: hit.id, title: hit.title, path: hit.path }))
      : [...(tree?.docs ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 8)
  ).filter((doc) => !already.has(doc.id))

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['docs'] })

  const link = useMutation({
    mutationFn: (docId: string) => api.docs.link(docId, taskId ? { taskId } : { specificationId }),
    onSuccess: () => {
      setPicking(false)
      setSearch('')
      refresh()
    },
  })
  const unlink = useMutation({
    mutationFn: (doc: LinkedDoc) => api.docs.unlink(doc.id, doc.linkId),
    onSuccess: refresh,
  })

  const failure = link.error ?? unlink.error

  return (
    <div className="stack stack--tight linked-docs">
      <span className="field__label">{t('docs.linked.title')}</span>

      {linked && linked.length === 0 && !picking && <span className="tiny muted">{t('docs.linked.none')}</span>}

      {linked && linked.length > 0 && (
        <ul className="linked-docs__list">
          {linked.map((doc) => (
            <li key={doc.linkId} className="linked-docs__item">
              <FileText size={13} aria-hidden className="muted" />
              <Link to={`/project/docs?doc=${doc.id}`} className="small truncate" title={doc.path}>
                {doc.title}
              </Link>
              <span className="tiny mono muted truncate linked-docs__path">{doc.path}</span>
              {canEdit && (
                <button
                  type="button"
                  className="docs-tree__tool"
                  onClick={() => unlink.mutate(doc)}
                  disabled={unlink.isPending}
                  title={t('docs.links.remove')}
                  aria-label={t('docs.links.remove')}
                >
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit &&
        (picking ? (
          <div className="linked-docs__picker">
            <div className="row row--tight">
              <input
                className="input"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('docs.linked.search')}
                aria-label={t('docs.linked.search')}
                autoFocus
              />
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => setPicking(false)}>
                {t('common.cancel')}
              </button>
            </div>
            {options.length === 0 ? (
              <span className="tiny muted">{term.length >= 2 ? t('docs.searchNone', { term }) : t('docs.linked.nothing')}</span>
            ) : (
              <ul className="linked-docs__options">
                {options.map((doc) => (
                  <li key={doc.id}>
                    <button type="button" className="linked-docs__option" onClick={() => link.mutate(doc.id)} disabled={link.isPending}>
                      <span className="small truncate">{doc.title}</span>
                      <span className="tiny mono muted truncate">{doc.path}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <button type="button" className="btn btn--sm btn--ghost linked-docs__add" onClick={() => setPicking(true)}>
            <Link2 size={13} aria-hidden /> {t('docs.linked.add')}
          </button>
        ))}

      {failure && <span className="field__error">{failure instanceof ApiError ? failure.message : t('error.generic')}</span>}
    </div>
  )
}
